import { BookOpen, TrendingUp, PieChart, LineChart, BarChart3, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: <Wallet size={48} />,
      title: 'Demat Account Opening',
      description:
        'Open your Demat account in just 10 minutes with zero charges. Quick KYC verification using Aadhaar and instant account activation.',
      features: ['Zero account opening fee', 'Instant digital KYC', 'Same-day activation', 'Paperless process'],
    },
    {
      icon: <TrendingUp size={48} />,
      title: 'Equity Trading',
      description:
        'Trade in NSE and BSE with advanced trading platforms. Access to 5000+ stocks with real-time data and research.',
      features: ['Zero brokerage on delivery', '₹20 per order intraday', 'Advanced charting tools', 'Real-time market data'],
    },
    {
      icon: <LineChart size={48} />,
      title: 'Futures & Options',
      description:
        'Trade in F&O segment with comprehensive tools and analytics. Access to all derivative instruments.',
      features: ['Low brokerage charges', 'Advanced option chains', 'Risk calculators', 'Strategy builder'],
    },
    {
      icon: <PieChart size={48} />,
      title: 'Mutual Funds',
      description:
        'Invest in 5000+ mutual funds with zero commission. Start SIPs with as low as ₹100 per month.',
      features: ['Zero commission', 'SIP from ₹100', 'Goal-based investing', 'One-click investing'],
    },
    {
      icon: <BarChart3 size={48} />,
      title: 'IPO Investment',
      description:
        'Apply for IPOs directly through the platform. Get expert analysis and recommendations for new listings.',
      features: ['Easy IPO application', 'Expert recommendations', 'IPO calendar', 'Instant allotment status'],
    },
    {
      icon: <BookOpen size={48} />,
      title: 'Research & Education',
      description:
        'Access comprehensive market research, trading courses, and expert insights to make informed decisions.',
      features: ['Daily market analysis', 'Trading tutorials', 'Webinars & workshops', 'Expert recommendations'],
    },
  ];

  const process = [
    {
      step: '01',
      title: 'Sign Up',
      description: 'Click on Open Account and fill in your basic details',
    },
    {
      step: '02',
      title: 'Complete KYC',
      description: 'Verify your identity using Aadhaar-based digital KYC',
    },
    {
      step: '03',
      title: 'Add Bank Details',
      description: 'Link your bank account for seamless fund transfers',
    },
    {
      step: '04',
      title: 'Start Trading',
      description: 'Your account is ready! Start investing immediately',
    },
  ];

  return (
    <div>
      <section className="py-24 md:py-32" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold mb-6"
              style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
              data-testid="services-heading"
            >
              Our Services
            </h1>
            <p className="text-base leading-relaxed" style={{ color: '#475569' }}>
              Comprehensive trading and investment solutions to help you achieve your financial goals
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, idx) => (
              <div
                key={idx}
                data-testid={`service-card-${idx}`}
                className="card-hover bg-[#FFFFFF] border border-[rgba(10,15,26,0.05)] p-8"
              >
                <div className="mb-6" style={{ color: '#FF5722' }}>
                  {service.icon}
                </div>
                <h3
                  className="text-xl sm:text-2xl font-semibold mb-3"
                  style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
                >
                  {service.title}
                </h3>
                <p className="text-base mb-6" style={{ color: '#475569' }}>
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, fidx) => (
                    <li key={fidx} className="text-sm flex items-center gap-2" style={{ color: '#475569' }}>
                      <span style={{ color: '#10B981' }}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-4"
              style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
            >
              How to Get Started
            </h2>
            <p className="text-base" style={{ color: '#475569' }}>
              Open your account in 4 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, idx) => (
              <div key={idx} data-testid={`process-step-${idx}`} className="text-center">
                <div
                  className="w-20 h-20 mx-auto mb-6 flex items-center justify-center"
                  style={{
                    backgroundColor: '#FFF4E6',
                    border: '2px solid #FF5722',
                  }}
                >
                  <span
                    className="text-2xl font-bold"
                    style={{ fontFamily: 'Outfit', color: '#FF5722' }}
                  >
                    {item.step}
                  </span>
                </div>
                <h3
                  className="text-xl font-semibold mb-3"
                  style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
                >
                  {item.title}
                </h3>
                <p className="text-base" style={{ color: '#475569' }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/brokerage-plans"
              data-testid="services-cta-button"
              className="btn-primary"
              style={{
                backgroundColor: '#FF5722',
                color: 'white',
                fontWeight: 600,
                padding: '1rem 2rem',
                textDecoration: 'none',
                display: 'inline-block',
              }}
            >
              Open Account Now
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-6"
            style={{ fontFamily: 'Outfit', color: '#0A0F1A' }}
          >
            Need Help Choosing?
          </h2>
          <p className="text-base mb-8" style={{ color: '#475569' }}>
            Our experts are here to guide you. Contact us via WhatsApp or use our chatbot for instant support.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Services;